import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useProductState } from './useProductState';
import type { ProductState } from './useProductState';
import { ProductConfigPanel } from './ProductConfigPanel';
import { ProductResultsPanel } from './ProductResultsPanel';
import { BuilderShell } from '@/components/builder/BuilderShell';
import { api } from '@/services/api';
import { endpoints } from '@/services/endpoints';
import { parseISO, differenceInDays, addDays, format as fmtDate } from 'date-fns';
import { useSavedReport } from '@/hooks/useSavedReport';
import { useSectionCache } from '@/hooks/useSectionCache';

interface EventFilterCondition {
  field: string;
  operator: string;
  value: string;
}

interface FieldDefinition {
  id: string;
  name: string;
  formula: string;
  events: string[];
  eventFilters?: Record<string, EventFilterCondition[]>;
}

/**
 * Product Report Builder.
 *
 * This is where users configure and visualize analytics queries.
 * All config is stored in the URL — making every view shareable.
 *
 * Layout:
 *   ┌─────────────────────────────────────────────────┐
 *   │  TopBar: title + actions (Save, Share, Run)     │
 *   ├─────────────────────────────────────────────────┤
 *   │  Config Panel (collapsible)                     │
 *   │  ┌───────────────────────────────────────────┐  │
 *   │  │ Table / Events / Date / Chart / Filters   │  │
 *   │  └───────────────────────────────────────────┘  │
 *   │                                                 │
 *   │  Results Panel                                  │
 *   │  ┌───────────────────────────────────────────┐  │
 *   │  │ Chart / Table visualization               │  │
 *   │  └───────────────────────────────────────────┘  │
 *   └─────────────────────────────────────────────────┘
 */
export function ProductBuilder() {
  const { reportId } = useParams();
  const { state, update, reset, canRun, toApiPayload, dates, comparisonDates, searchParams } = useProductState();

  // ─── Load saved report ────────────────────────────────────────────────
  const { savedReport } = useSavedReport<ProductState>({
    reportId,
    sectionId: 'product',
    searchParams,
    update,
    mapConfig: useCallback((config: Record<string, unknown>) => {
      const rawEvents = config.eventNames ?? config.events ?? '';
      const events: string[] = Array.isArray(rawEvents)
        ? rawEvents
        : typeof rawEvents === 'string' && rawEvents
          ? rawEvents.split(',').filter(Boolean)
          : [];
      const CHART_LONG_TO_SHORT: Record<string, string> = { time_series: 'ts', pivot: 'pivot' };
      const INTERVAL_LONG_TO_SHORT: Record<string, string> = { hours: 'h', days: 'd', weeks: 'w', months: 'm' };
      return {
        table: ((config.tableName as string) || (config.table as string) || ''),
        events,
        preset: ((config.datePreset as string) || (config.preset as string) || ''),
        start: ((config.startDate as string) || (config.start as string) || ''),
        end: ((config.endDate as string) || (config.end as string) || ''),
        chart: (CHART_LONG_TO_SHORT[(config.chartType as string)] || (config.chart as string) || 'ts') as ProductState['chart'],
        interval: (INTERVAL_LONG_TO_SHORT[(config.timeInterval as string)] || (config.interval as string) || 'd'),
        count: ((config.countMethod as string) || (config.count as string) || 'all'),
        metric: ((config.metricField as string) || (config.metric as string) || ''),
        calc: ((config.calculationMethod as string) || (config.calc as string) || ''),
        breakdown: ((config.breakdownProperty as string) || (config.breakdown as string) || ''),
        row: ((config.rowDimension as string) || (config.row as string) || ''),
        col: ((config.columnBreakdown as string) || (config.col as string) || ''),
        formulas: (() => {
          const raw = config.formulaIds ?? config.formulas ?? config.formulaId ?? config.formula ?? '';
          if (Array.isArray(raw)) return raw.filter(Boolean) as string[];
          return typeof raw === 'string' && raw ? raw.split(',').filter(Boolean) : [];
        })(),
        filters: typeof config.filters === 'string'
          ? config.filters
          : Array.isArray(config.filters) && config.filters.length > 0
            ? btoa(JSON.stringify(config.filters))
            : '',
      };
    }, []),
  });

  // ─── Load all selected custom formula field definitions ──────────────────
  const { data: allFieldsData } = useQuery<{ fields: FieldDefinition[] }>({
    queryKey: ['fields', 'product'],
    queryFn: () => api.get<{ fields: FieldDefinition[] }>(endpoints.fields.list('product')),
    staleTime: 30_000,
    enabled: state.formulas.length > 0,
  });

  // Active fields: only those currently selected
  const activeFields = useMemo(() => {
    if (!allFieldsData?.fields?.length || !state.formulas.length) return [];
    return state.formulas
      .map((id) => allFieldsData.fields.find((f) => f.id === id))
      .filter(Boolean) as FieldDefinition[];
  }, [allFieldsData, state.formulas]);

  // Events contributed by formula fields only (used to populate breakdowns even when no events selected)
  const formulaEvents = useMemo(() => {
    const set = new Set<string>();
    for (const field of activeFields) {
      for (const ev of field.events) set.add(ev);
    }
    return Array.from(set);
  }, [activeFields]);

  // Merge formula events from ALL active fields with user-selected events (union, deduped)
  const mergedEvents = useMemo(() => {
    const combined = new Set([...state.events, ...formulaEvents]);
    return Array.from(combined);
  }, [state.events, formulaEvents]);

  // ─── Run query ────────────────────────────────────────────────────────
  // Two-pass approach when per-event filters exist on any field:
  //   1. Main query: all events (user + formula), NO per-event filters → display data
  //   2. Per-field formula queries: only field-specific events WITH per-event filters
  // This keeps display counts unfiltered while formulas use filtered counts.
  // ─── Section cache (preserve results across navigation) ─────────────
  const [cachedData, setCachedData] = useSectionCache<unknown>('product-builder');

  const queryMutation = useMutation({
    mutationFn: async () => {
      const payload = toApiPayload();
      // Main query always includes all merged events, no per-event filters
      payload.eventNames = mergedEvents;

      const endpoint = state.chart === 'ts'
        ? endpoints.analytics.timeSeries()
        : endpoints.analytics.pivot();

      // Fire main + comparison queries in parallel when comparison dates are set
      let comparisonPromise: ReturnType<typeof api.post> | null = null;
      if (comparisonDates) {
        if (state.chart === 'pivot') {
          comparisonPromise = api.post(endpoint, { ...payload, startDate: comparisonDates.startDate, endDate: comparisonDates.endDate });
        } else if (state.chart === 'ts') {
          // For time series, compute comparison end = cmpStart + primary range length
          const pStart = parseISO(dates.startDate);
          const pEnd = parseISO(dates.endDate);
          const rangeDays = differenceInDays(pEnd, pStart);
          const cmpStartDate = comparisonDates.startDate;
          const cmpEndDate = fmtDate(addDays(parseISO(cmpStartDate), rangeDays), 'yyyy-MM-dd');
          comparisonPromise = api.post(endpoint, { ...payload, startDate: cmpStartDate, endDate: cmpEndDate });
        }
      }

      const mainResult = await api.post(endpoint, payload);

      // For each active field with per-event filters, run a supplementary query
      const formulaDataMap: Record<string, Record<string, unknown>> = {};
      const fieldsWithFilters = activeFields.filter(
        (f) => f.eventFilters && Object.keys(f.eventFilters).length > 0,
      );

      if (fieldsWithFilters.length > 0) {
        const promises = fieldsWithFilters.map(async (field) => {
          const formulaPayload = { ...payload };
          formulaPayload.eventNames = field.events;
          (formulaPayload as Record<string, unknown>).formulaEventFilters = field.eventFilters;
          try {
            const result = await api.post(endpoint, formulaPayload) as Record<string, unknown>;
            formulaDataMap[field.id] = result;
          } catch {
            console.warn(`[ProductBuilder] Formula query failed for field ${field.name}`);
          }
        });
        await Promise.all(promises);
      }

      const comparisonResult = comparisonPromise ? await comparisonPromise : undefined;

      return {
        ...(mainResult as Record<string, unknown>),
        _formulaDataMap: Object.keys(formulaDataMap).length > 0 ? formulaDataMap : undefined,
        _comparisonData: comparisonResult ?? undefined,
      };
    },
    onSuccess: (data) => {
      setCachedData(data);
    },
  });

  // Effective data: mutation data takes priority, then cached
  const effectiveData = queryMutation.data ?? cachedData;
  const effectiveHasRun = queryMutation.isSuccess || queryMutation.isError || !!cachedData;

  const handleRun = useCallback(() => {
    if (canRun) {
      queryMutation.mutate();
    }
  }, [canRun, queryMutation]);

  return (
    <BuilderShell
      title="New Report"
      subtitle="Product"
      sectionId="product"
      sectionRoute="/product"
      reportId={reportId}
      savedReport={savedReport as { report?: { name?: string; description?: string } } | undefined}
      onReset={reset}
      onRun={handleRun}
      isRunning={queryMutation.isPending}
      canRun={canRun}
      configForSave={state as unknown as Record<string, unknown>}
      configPanel={<ProductConfigPanel state={state} onChange={update} dates={dates} formulaEvents={formulaEvents} />}
    >
      <ProductResultsPanel
        chartType={state.chart}
        isLoading={queryMutation.isPending}
        isError={queryMutation.isError}
        error={queryMutation.error}
        data={effectiveData}
        hasRun={effectiveHasRun}
        canRun={canRun}
        onRun={handleRun}
        formulaFields={activeFields.map((f) => ({ id: f.id, name: f.name, formula: f.formula, events: f.events }))}
        formulaDataMap={(effectiveData as Record<string, unknown> | undefined)?._formulaDataMap as Record<string, Record<string, unknown>> | undefined}
        selectedEvents={state.events}
        comparisonData={(effectiveData as Record<string, unknown> | undefined)?._comparisonData as Record<string, unknown> | undefined}
        cumulative={state.chart === 'ts' ? state.cumulative : undefined}
      />
    </BuilderShell>
  );
}
